import { useEffect, useRef } from "react";

export default function HeroSection() {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/9d084227bd109ba0fd37f5b0caf78342.jpg"
          alt="Hero background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070d1a]/95 via-[#070d1a]/80 to-[#070d1a]/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070d1a] via-transparent to-[#070d1a]/30" />
      </div>

      {/* Animated gold grid pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Floating gold orbs */}
      <div className="absolute top-32 right-24 w-64 h-64 rounded-full opacity-10 blur-3xl bg-[#D4AF37] animate-pulse" />
      <div className="absolute bottom-32 left-20 w-48 h-48 rounded-full opacity-10 blur-3xl bg-[#D4AF37] animate-pulse" style={{ animationDelay: "1.5s" }} />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-24">
        <div className="max-w-3xl">
          {/* Tags */}
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <div className="inline-flex items-center gap-2 border border-[#D4AF37]/40 rounded-full px-4 py-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
              <span className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Digital Revenue Systems
              </span>
            </div>
            <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 rounded-full px-4 py-1.5">
              <span className="text-base leading-none">🇪🇸</span>
              <span className="text-white/80 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Se Habla Español
              </span>
            </div>
          </div>

          {/* Headline */}
          <h1
            className="text-white font-black leading-none mb-6"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "clamp(48px, 7vw, 92px)",
              letterSpacing: "-2px",
            }}
          >
            Turn Your Business
            <br />
            Into a{" "}
            <span
              className="relative inline-block"
              style={{
                background: "linear-gradient(135deg, #D4AF37 0%, #f5d980 50%, #D4AF37 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Customer Machine
            </span>
          </h1>

          {/* Subheadline */}
          <p
            className="text-white/75 text-xl md:text-2xl leading-relaxed mb-12 max-w-xl"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            We build websites that bring you clients —<br />even while you sleep.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => scrollTo("cta")}
              className="group flex items-center justify-center gap-3 bg-[#D4AF37] hover:bg-[#c4a030] text-[#070d1a] font-bold text-base px-10 py-5 rounded-full tracking-wider uppercase transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-[#D4AF37]/30 hover:shadow-[#D4AF37]/50 hover:scale-105"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Get My Website
              <span className="w-7 h-7 flex items-center justify-center bg-[#070d1a]/20 rounded-full group-hover:translate-x-1 transition-transform duration-300">
                <i className="ri-arrow-right-line text-sm" />
              </span>
            </button>
            <button
              onClick={() => scrollTo("portfolio")}
              className="group flex items-center justify-center gap-3 bg-transparent border-2 border-[#D4AF37]/60 hover:border-[#D4AF37] text-white font-bold text-base px-10 py-5 rounded-full tracking-wider uppercase transition-all duration-300 cursor-pointer whitespace-nowrap hover:bg-[#D4AF37]/10"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              View Portfolio
              <span className="w-7 h-7 flex items-center justify-center border border-[#D4AF37]/60 rounded-full group-hover:translate-x-1 transition-transform duration-300">
                <i className="ri-arrow-right-line text-sm text-[#D4AF37]" />
              </span>
            </button>
          </div>

          {/* Stats bar */}
          <div className="flex flex-wrap gap-8 mt-16 pt-12 border-t border-white/10">
            {[
              { value: "50+", label: "Projects Delivered" },
              { value: "100%", label: "Mobile-First" },
              { value: "3x", label: "Avg. Client Growth" },
            ].map((stat) => (
              <div key={stat.label}>
                <div
                  className="text-3xl font-black text-[#D4AF37]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {stat.value}
                </div>
                <div className="text-white/50 text-sm mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/30 text-xs tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Scroll
        </span>
        <i className="ri-arrow-down-line text-[#D4AF37]/60 text-lg" />
      </div>
    </section>
  );
}
